/**
 * Helpers for Instagram profile posts and display names.
 *
 * Since 2026 Instagram's web profile answer (web_profile_info, behind both IGFlashAPI and StarAPI) carries the post
 * count but no posts: `edge_owner_to_timeline_media.edges` is always empty (measured 2026-10-29 on natgeo and nasa,
 * both suppliers). The posts now come from the user feed endpoint (Flash /ig/posts/, StarAPI /instagram/user/get_media),
 * which both answer with the same Instagram v1 feed items. `mapFeedItemToPost` turns one item into the post shape the
 * profile lookup has always returned.
 *
 * IGFlashAPI also replaces every display name with a placeholder ("Access delayed. Only the owner of the page can
 * access this information"), on profiles, followers, similar accounts and feed authors alike. That text is not a
 * name: `cleanFullName` turns it into "no name" so it is never stored or shown.
 */

import type { InstagramAlbumChildPostProps, InstagramPostProps } from "."

const PLACEHOLDER_FULL_NAME = /^\s*access delayed\.\s*only the owner of the page/i

/** True for the supplier's "Access delayed. Only the owner of the page can access this information" placeholder. */
export function isPlaceholderFullName(value: unknown): boolean {
    return typeof value === "string" && PLACEHOLDER_FULL_NAME.test(value)
}

/** The display name, or undefined when the supplier sent its placeholder instead of a name. */
export function cleanFullName<T>(value: T): T | undefined {
    return isPlaceholderFullName(value) ? undefined : value
}

/**
 * The value to store in `accounts.name` (NOT NULL): the display name, or "" when the profile has none, the supplier sent
 * its placeholder, or the field is not a string. Every accounts insert goes through this, so a nameless profile can never
 * fail the insert (2026-11-02/03: three paid Apple Pay checkouts and 80 checkout-recovery retries failed on name = null).
 */
export function accountNameOrBlank(value: unknown): string {
    return typeof value === "string" && !isPlaceholderFullName(value) ? value : ""
}

/** Drops a placeholder `full_name` from a profile (`{ graphql: { user } }`) in place. Returns the same object. */
export function sanitizeProfileFullName<T>(profile: T): T {
    const user = (profile as any)?.graphql?.user
    if (user && isPlaceholderFullName(user.full_name)) delete user.full_name
    return profile
}

/** Drops placeholder `full_name`s from a list of users (followers, similar accounts) in place. Non-arrays pass through. */
export function sanitizeUserListFullNames<T>(users: T): T {
    if (Array.isArray(users)) {
        for (const user of users) {
            if (user && typeof user === "object" && isPlaceholderFullName((user as any).full_name)) delete (user as any).full_name
        }
    }
    return users
}

/**
 * True when a profile asked for with posts came back without any and a feed call could still find some: a public
 * account with a numeric ID whose post count is not 0, not already checked (`posts_checked`).
 */
export function needsPostsFill(user: any): boolean {
    if (!user || typeof user !== "object") return false
    if (Array.isArray(user.edge_owner_to_timeline_media) && user.edge_owner_to_timeline_media.length > 0) return false
    if (user.posts_checked === true) return false
    if (user.is_private === true) return false
    if (user.media_count === 0) return false
    return /^\d+$/.test(String(user.id ?? ""))
}

const firstImageUrl = (media: any): string | undefined => media?.image_versions2?.candidates?.[0]?.url || undefined
const firstVideoUrl = (media: any): any => media?.video_versions?.[0]?.url || undefined
const viewCount = (media: any): number | null => {
    const value = media?.play_count ?? media?.ig_play_count ?? media?.view_count ?? media?.video_view_count
    return typeof value === "number" && Number.isFinite(value) ? value : null
}

/** One Instagram v1 feed item (Flash /ig/posts/ or StarAPI get_media) as a profile post. */
export function mapFeedItemToPost(item: any): InstagramPostProps {
    const image = firstImageUrl(item) ?? firstImageUrl(item?.carousel_media?.[0])
    const children: InstagramAlbumChildPostProps[] | undefined = Array.isArray(item?.carousel_media) && item.carousel_media.length
        ? item.carousel_media.map((child: any) => ({
              id: child?.code || String(child?.id ?? child?.pk ?? ""),
              image_url: firstImageUrl(child),
              is_video: child?.media_type === 2,
              video_url: firstVideoUrl(child) ?? null,
              video_view_count: viewCount(child),
              accessibility_caption: child?.accessibility_caption || null
          }))
        : undefined

    return {
        id: item?.code || String(item?.pk ?? item?.id ?? ""),
        caption: item?.caption?.text || null,
        location: item?.location?.name || null,
        image_url: image,
        hd_image_url: image,
        like_count: typeof item?.like_count === "number" ? item.like_count : 0,
        comment_count: typeof item?.comment_count === "number" ? item.comment_count : 0,
        video_view_count: viewCount(item),
        taken_at: item?.taken_at,
        accessibility_caption: item?.accessibility_caption || null,
        is_video: item?.media_type === 2,
        video_url: firstVideoUrl(item) ?? null,
        is_album: item?.media_type === 8,
        album_children: children ?? null
    } as InstagramPostProps
}
